import { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { Outlet, useParams } from 'react-router-dom';

import { useLoginMutation, useSelectCompanyMutation } from '../../service';
import { setSelectedCompany } from '../../slice';
import { useLanguage } from '@/app/language/useLanguage';

import { Buffer } from 'buffer';

const AUTH_COOKIE_NAME = 'thunderbolt_auth=';

/*
  This component is temporary workaround for logging in OneTrust bot to check Thunderbolt's cookies.
  It uses user credentials from cookie to log in user.

  It will be removed after OneTrust finishes their work on cookie scan.
  Related ticket: https://getgoing.atlassian.net/browse/OBT-2534
*/
export function AuthWithCookie() {
  const dispatch = useDispatch();
  const [login] = useLoginMutation();
  const [selectCompany] = useSelectCompanyMutation();
  const { companyLogin: companyName } = useParams();
  const { locale } = useLanguage();

  const loginWithCookieCredentials = useCallback(
    async (email: string, password: string) => {
      try {
        const loginResult = await login({
          email,
          password,
          locale,
          companyName,
        }).unwrap();
        if (!loginResult || !loginResult.companies) return;

        const { companies, loginToken } = loginResult;
        const company = companies[0];

        await selectCompany({
          loginToken,
          companyId: company.companyId,
        }).unwrap();

        dispatch(setSelectedCompany({ selectedCompany: company }));
      } catch {}
    },
    [login, locale, companyName, selectCompany, dispatch],
  );

  useEffect(() => {
    const authCookie = document.cookie
      .split('; ')
      .find((cookie) => cookie.startsWith(AUTH_COOKIE_NAME))
      ?.replace(AUTH_COOKIE_NAME, '');

    if (authCookie) {
      const [email, password] = Buffer.from(authCookie, 'base64').toString().split(':');

      Yup.string()
        .email()
        .validate(email)
        .then(() => loginWithCookieCredentials(email, password))
        .catch(() => {});
    }
  }, [loginWithCookieCredentials]);

  return <Outlet />;
}
